import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  ImagesBox,
  TextAreaBox,
  TextPlaceholder,
} from '../VariationTwo';
import { Filler } from '../shared';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  question: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  answerFontSize: number;
  brakes: boolean;
  quotes: boolean;
  fontSize?: number;
}

const StyledUploadedImages = styled(UploadedImages)`
  height: 274px;
`;

const StyledTextArea = styled(Textarea)`
  height: 60px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 250px;
`;

const AnswersBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 24px;
  border-top: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const AnswerFontSizeHolder = styled.div`
  grid-column: span 2;
`;

interface Props {
  inArticle?: boolean;
}

const VariationMillionaireTrivia: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [brakes, setBrakes] = useState(false);
  const [quotes, setQuotes] = useState(true);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [fontSize, setFontSize] = useState('');
  const [answerFontSize, setAnswerFontSize] = useState('40');
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [AnswerA, setAnswerA] = useState('');
  const [AnswerB, setAnswerB] = useState('');
  const [AnswerC, setAnswerC] = useState('');
  const [AnswerD, setAnswerD] = useState('');

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setFontSize('');
    setAnswerA('');
    setAnswerB('');
    setAnswerC('');
    setAnswerD('');
    setAnswerFontSize('40');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      question: text,
      answerA: AnswerA,
      answerB: AnswerB,
      answerC: AnswerC,
      answerD: AnswerD,
      brakes: brakes,
      quotes: quotes,
      divider: leftImage.divider,
      answerFontSize: parseInt(answerFontSize),
      fontSize: parseInt(fontSize),
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      hasText={false}
    />
  ));

  const isResetDisabled =
    !leftImages?.length &&
    !text &&
    !brakes &&
    !fontSize &&
    !AnswerA &&
    !AnswerB &&
    !AnswerC &&
    !AnswerD &&
    answerFontSize === '40';

  return (
    <>
      <Box>
        <Filler />
        <Header>
          <div>
            <Heading $type="button">Millionaire</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one image on the left side and add heading with 4 answers
              on the right with the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <ImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <StyledUploadedImages>
                    {renderLeftImages}
                  </StyledUploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowMoreImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <StyledUploadMultipleImages
                  coverIndex={2}
                  setImages={setLeftImages}
                />
              )}
            </ImagesBox>
            <div>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Question</LabelText>
                  <StyledTextArea
                    onChange={(e) => setText(e.target.value)}
                    value={text}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={quotes}
                      onChange={() => setQuotes((prev) => !prev)}
                    >
                      Quotes
                    </Checkbox>
                    <Checkbox
                      checked={brakes}
                      onChange={() => setBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
              </TextAreaBox>
              {!!leftImages?.length && <TextPlaceholder />}
            </div>
          </ImageGrid>
        )}
        <AnswersBox>
          <div>
            <LabelText $type="smallHeadline">Answer A</LabelText>
            <Input
              value={AnswerA}
              onChange={(e) => setAnswerA(e.target.value)}
              placeholder="Answer A"
              type="text"
            />
          </div>
          <div>
            <LabelText $type="smallHeadline">Answer B</LabelText>
            <Input
              value={AnswerB}
              onChange={(e) => setAnswerB(e.target.value)}
              placeholder="Answer B"
              type="text"
            />
          </div>
          <div>
            <LabelText $type="smallHeadline">Answer C</LabelText>
            <Input
              value={AnswerC}
              onChange={(e) => setAnswerC(e.target.value)}
              placeholder="Answer C"
              type="text"
            />
          </div>
          <div>
            <LabelText $type="smallHeadline">Answer D</LabelText>
            <Input
              value={AnswerD}
              onChange={(e) => setAnswerD(e.target.value)}
              placeholder="Answer D"
              type="text"
            />
          </div>
          <AnswerFontSizeHolder>
            <LabelText $type="smallHeadline">Answers Font Size</LabelText>
            <Input
              value={answerFontSize}
              onChange={(e) => setAnswerFontSize(e.target.value)}
              placeholder="Font Size (px)"
              type="number"
              min="0"
            />
          </AnswerFontSizeHolder>
        </AnswersBox>
      </Box>
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationMillionaireTrivia;
